// Iter 128 — "Share my month" modal.
// Renders an off-screen styled share card (1200×1200 IG-square layout),
// captures it as a PNG via html-to-image, and exposes download + share
// intents for Twitter / WhatsApp / Telegram / Facebook / LinkedIn.
//
// Every share carries an `?ref=USER_ID` referral on the share URL so this
// doubles as organic acquisition.
import { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Download, Link2, Twitter, Send, MessageCircle, Facebook, Linkedin, Loader2,
} from "lucide-react";
import { getCurrency, fmtAmount } from "../lib/currency";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const SITE_URL = "https://ndakubet.com";

const buildReferralUrl = (userId) => {
  if (!userId) return SITE_URL;
  return `${SITE_URL}/?ref=${encodeURIComponent(userId)}`;
};

const buildShareCopy = (monthLabel, pnl, roi, winRate, currency) => {
  const pnlStr = fmtAmount(pnl, currency, { compact: false });
  const sign = pnl >= 0 ? "📈" : "📉";
  return `${sign} ${monthLabel} on NDAKUBET: ${pnlStr} net P/L · ${roi}% ROI · ${winRate}% win rate. Built on AI football intelligence.`;
};

export const ShareMonthModal = ({
  open, onOpenChange, monthlyData, summary, year, month, userId, currency,
}) => {
  const cardRef = useRef(null);
  const [pngUrl, setPngUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const monthLabel = `${MONTHS[month]} ${year}`;
  const c = getCurrency(currency);
  const s = monthlyData?.summary || {};
  const pnl = s.pnl ?? 0;
  const roi = s.roi_pct ?? 0;
  const winDays = s.win_days ?? 0;
  const lossDays = s.loss_days ?? 0;
  const daysTracked = s.days_tracked ?? 0;
  const winRate = daysTracked > 0 ? Math.round((winDays / daysTracked) * 100) : 0;
  const currentStreak = summary?.current_streak ?? 0;
  const bestStreak = summary?.best_streak ?? 0;
  const referralUrl = buildReferralUrl(userId);
  const shareCopy = buildShareCopy(monthLabel, pnl, roi, winRate, currency);

  // Pre-build the calendar grid for the card.
  const grid = useMemo(() => {
    if (!monthlyData) return [];
    const map = {};
    (monthlyData.entries || []).forEach((e) => { map[e.date] = e; });
    const cells = [];
    for (let i = 0; i < (monthlyData.first_weekday || 0); i++) cells.push(null);
    for (let d = 1; d <= monthlyData.days_in_month; d++) {
      const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, entry: map[date] });
    }
    return cells;
  }, [monthlyData, year, month]);

  // Render PNG whenever the modal opens with valid data.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPngUrl(null);
    // The ref may be null on the first scheduled tick (Dialog mounts via
    // portal — ref attaches after the next paint), so retry every 150ms
    // until the node is in the DOM or we've waited ~1.8s.
    const tryRender = async (attempt = 1) => {
      if (cancelled) return;
      const node = cardRef.current;
      if (!node) {
        if (attempt < 12) setTimeout(() => tryRender(attempt + 1), 150);
        return;
      }
      try {
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        const dataUrl = await toPng(node, {
          pixelRatio: 2,
          cacheBust: false,
          skipFonts: true,    // CORS-blocked Uber fonts would hang the embed step
          width: 1080,
          height: 1080,
          backgroundColor: "#0b0f14",
          filter: (n) => {
            if (!n || !n.tagName) return true;
            const tag = n.tagName.toUpperCase();
            return tag !== "LINK" && tag !== "STYLE";
          },
        });
        if (!cancelled) setPngUrl(dataUrl);
      } catch (e) {
        console.error("[share] png render failed", e);
        if (!cancelled) toast.error("Could not generate share image");
      }
    };
    const t = setTimeout(() => tryRender(1), 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, monthlyData, summary, currency]);

  const handleDownload = () => {
    if (!pngUrl) return;
    const link = document.createElement("a");
    link.download = `ndakubet-${year}-${String(month + 1).padStart(2, "0")}.png`;
    link.href = pngUrl;
    link.click();
    toast.success("Image downloaded");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success("Referral link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const openIntent = (kind) => {
    setBusy(true);
    const enc = encodeURIComponent;
    const text = enc(`${shareCopy}\n\n${referralUrl}`);
    const url = enc(referralUrl);
    const title = enc(shareCopy);
    const targets = {
      twitter:  `https://twitter.com/intent/tweet?text=${text}`,
      whatsapp: `https://wa.me/?text=${text}`,
      telegram: `https://t.me/share/url?url=${url}&text=${title}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    window.open(targets[kind], "_blank", "noopener,width=600,height=600");
    setTimeout(() => setBusy(false), 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl bg-[#0b0f14] border-white/[0.08] max-h-[92vh] overflow-y-auto"
        data-testid="share-month-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Share {monthLabel}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Auto-generated image with your monthly stats — perfect for stories &amp; group chats.
          </p>
        </DialogHeader>

        {/* PREVIEW: rendered PNG (or live card while generating) */}
        <div className="rounded-2xl overflow-hidden bg-[#06080b] border border-white/[0.05]">
          {pngUrl ? (
            <img
              src={pngUrl}
              alt="Share preview"
              className="w-full h-auto block"
              data-testid="share-month-preview"
            />
          ) : (
            <div className="aspect-square flex items-center justify-center text-muted-foreground gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Generating image…
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
          <Button
            onClick={handleDownload}
            disabled={!pngUrl}
            data-testid="share-download-btn"
            className="bg-emerald-400 hover:bg-emerald-300 text-emerald-950 font-bold h-11 rounded-full col-span-2 sm:col-span-1"
          >
            <Download className="w-4 h-4 mr-1.5" /> Download PNG
          </Button>
          <Button
            variant="outline"
            onClick={handleCopyLink}
            data-testid="share-copy-link-btn"
            className="h-11 rounded-full border-white/15 bg-white/[0.04] hover:bg-white/[0.08]"
          >
            <Link2 className="w-4 h-4 mr-1.5" /> Copy link
          </Button>
          <Button
            variant="outline"
            onClick={() => openIntent("twitter")}
            disabled={busy}
            data-testid="share-twitter-btn"
            className="h-11 rounded-full border-white/15 bg-white/[0.04] hover:bg-white/[0.08]"
          >
            <Twitter className="w-4 h-4 mr-1.5 text-sky-400" /> X / Twitter
          </Button>
          <Button
            variant="outline"
            onClick={() => openIntent("whatsapp")}
            disabled={busy}
            data-testid="share-whatsapp-btn"
            className="h-11 rounded-full border-white/15 bg-white/[0.04] hover:bg-white/[0.08]"
          >
            <MessageCircle className="w-4 h-4 mr-1.5 text-emerald-400" /> WhatsApp
          </Button>
          <Button
            variant="outline"
            onClick={() => openIntent("telegram")}
            disabled={busy}
            data-testid="share-telegram-btn"
            className="h-11 rounded-full border-white/15 bg-white/[0.04] hover:bg-white/[0.08]"
          >
            <Send className="w-4 h-4 mr-1.5 text-cyan-400" /> Telegram
          </Button>
          <Button
            variant="outline"
            onClick={() => openIntent("facebook")}
            disabled={busy}
            data-testid="share-facebook-btn"
            className="h-11 rounded-full border-white/15 bg-white/[0.04] hover:bg-white/[0.08]"
          >
            <Facebook className="w-4 h-4 mr-1.5 text-blue-400" /> Facebook
          </Button>
          <Button
            variant="outline"
            onClick={() => openIntent("linkedin")}
            disabled={busy}
            data-testid="share-linkedin-btn"
            className="h-11 rounded-full border-white/15 bg-white/[0.04] hover:bg-white/[0.08]"
          >
            <Linkedin className="w-4 h-4 mr-1.5 text-blue-300" /> LinkedIn
          </Button>
        </div>

        <p className="text-[10px] text-muted-foreground text-center mt-2 uppercase tracking-[0.18em]">
          Your unique referral · friends who sign up help your stats stand out
        </p>

        {/* OFF-SCREEN render target — moved off the viewport but kept in
            the layout flow so html-to-image can capture it with full styles. */}
        <div
          aria-hidden
          style={{
            position: "fixed",
            top: "-10000px",
            left: "-10000px",
            pointerEvents: "none",
          }}
        >
          <div ref={cardRef}>
            <ShareCard
              monthLabel={monthLabel}
              pnl={pnl}
              roi={roi}
              winRate={winRate}
              winDays={winDays}
              lossDays={lossDays}
              daysTracked={daysTracked}
              currentStreak={currentStreak}
              bestStreak={bestStreak}
              grid={grid}
              currency={c}
              referralUrl={referralUrl.replace(/^https?:\/\//, "")}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ─── ShareCard ───
// 1080×1080 square card optimised for Instagram / WhatsApp / FB stories.
// Uses inline styles only so html-to-image captures it identically across
// browsers (no Tailwind purge / CSS-variable surprises).
const ShareCard = ({
  monthLabel, pnl, roi, winRate, winDays, lossDays, daysTracked,
  currentStreak, bestStreak, grid, currency, referralUrl,
}) => {
  const isUp = pnl >= 0;
  const headlineColor = isUp ? "#34d399" : "#fb7185";
  const pnlStr = fmtAmount(pnl, currency.code, { compact: false });
  const SIZE = 1080;

  return (
    <div
      style={{
        width: SIZE,
        height: SIZE,
        background: "linear-gradient(160deg, #0f1419 0%, #0a0d11 60%, #060809 100%)",
        color: "#e9edf3",
        fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
        position: "relative",
        padding: 72,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* radial accents */}
      <div style={{
        position: "absolute", top: -200, right: -200, width: 600, height: 600,
        background: `radial-gradient(circle, ${isUp ? "rgba(52,211,153,0.18)" : "rgba(251,113,133,0.18)"} 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: -250, left: -150, width: 600, height: 600,
        background: "radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 60%)",
        pointerEvents: "none",
      }} />

      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: "#1a1f27",
            border: "1px solid rgba(255,255,255,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 800, color: "#a3e635",
          }}>N</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 22, letterSpacing: "-0.01em" }}>NDAKUBET</div>
            <div style={{ fontSize: 10, color: "#7a8392", textTransform: "uppercase", letterSpacing: "0.22em", fontWeight: 700 }}>
              AI Football Intel
            </div>
          </div>
        </div>
        <div style={{
          fontSize: 11, color: "#a3e635", letterSpacing: "0.22em", textTransform: "uppercase",
          fontWeight: 700, padding: "8px 16px", borderRadius: 999,
          background: "rgba(163,230,53,0.08)", border: "1px solid rgba(163,230,53,0.20)",
        }}>
          {currency.flag} {currency.code}
        </div>
      </div>

      {/* HERO */}
      <div style={{ marginTop: 48, zIndex: 2 }}>
        <div style={{ fontSize: 14, color: "#7a8392", textTransform: "uppercase", letterSpacing: "0.22em", fontWeight: 700 }}>
          My {monthLabel}
        </div>
        <div style={{
          marginTop: 14,
          fontSize: 144,
          lineHeight: 1,
          fontWeight: 800,
          color: headlineColor,
          letterSpacing: "-0.04em",
        }}>
          {pnlStr}
        </div>
        <div style={{ marginTop: 16, fontSize: 22, color: "#a8b2c1", fontWeight: 500 }}>
          {roi >= 0 ? "+" : ""}{roi.toFixed(1)}% ROI · {winRate}% win rate · {daysTracked} day{daysTracked !== 1 ? "s" : ""} tracked
        </div>
      </div>

      {/* STAT STRIP */}
      <div style={{ marginTop: 36, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, zIndex: 2 }}>
        <StatTile label="Wins" value={`${winDays}`} accent="#34d399" />
        <StatTile label="Current streak" value={`${currentStreak}d`} accent="#f59e0b" />
        <StatTile label="Best streak" value={`${bestStreak}d`} accent="#a78bfa" />
      </div>

      {/* CALENDAR GRID */}
      <div style={{ marginTop: 36, zIndex: 2, flex: 1 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            <div key={i} style={{
              fontSize: 12, fontWeight: 700, color: "#7a8392",
              textAlign: "center", letterSpacing: "0.2em", paddingBottom: 6,
            }}>{d}</div>
          ))}
          {grid.map((cell, i) => <CalendarCell key={i} cell={cell} />)}
        </div>
      </div>

      {/* FOOTER */}
      <div style={{
        marginTop: 28, paddingTop: 22, borderTop: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        zIndex: 2,
      }}>
        <div style={{ fontSize: 14, color: "#a8b2c1" }}>
          <span style={{ fontWeight: 700, color: "#e9edf3" }}>Track yours →</span>
          &nbsp;<span style={{ color: "#a3e635", fontWeight: 700 }}>{referralUrl}</span>
        </div>
        <div style={{
          fontSize: 11, color: "#7a8392", textTransform: "uppercase",
          letterSpacing: "0.22em", fontWeight: 700,
        }}>
          Bet responsibly · 18+
        </div>
      </div>
    </div>
  );
};

const StatTile = ({ label, value, accent }) => (
  <div style={{
    padding: "20px 22px",
    borderRadius: 18,
    background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.06)",
  }}>
    <div style={{ fontSize: 11, color: "#7a8392", textTransform: "uppercase", letterSpacing: "0.18em", fontWeight: 700 }}>
      {label}
    </div>
    <div style={{ marginTop: 8, fontSize: 36, fontWeight: 800, color: accent, letterSpacing: "-0.02em" }}>
      {value}
    </div>
  </div>
);

const CalendarCell = ({ cell }) => {
  if (!cell) return <div style={{ aspectRatio: "1/1" }} />;
  const e = cell.entry;
  const has = !!e && typeof e.pnl === "number";
  const up = has && e.pnl > 0;
  const down = has && e.pnl < 0;
  const bg = up
    ? "rgba(52,211,153,0.16)"
    : down
    ? "rgba(251,113,133,0.14)"
    : "rgba(255,255,255,0.025)";
  const border = up
    ? "rgba(52,211,153,0.40)"
    : down
    ? "rgba(251,113,133,0.40)"
    : "rgba(255,255,255,0.06)";
  const numColor = up ? "#6ee7b7" : down ? "#fda4af" : "#7a8392";

  return (
    <div style={{
      aspectRatio: "1/1",
      borderRadius: 12,
      background: bg,
      border: `1px solid ${border}`,
      padding: 8,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "stretch",
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: numColor }}>{cell.day}</div>
      {has && (
        <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: up ? "#34d399" : "#fb7185",
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}>
          {e.pnl > 0 ? "+" : "-"}{Math.abs(e.pnl).toFixed(0)}
        </div>
      )}
    </div>
  );
};

export default ShareMonthModal;
